'use client'

import { useQuery } from '@apollo/client/react'
import { type FC, useState } from 'react'

import { TypeStyleHeadlineExtraSmall } from '@syconium/little-miss-figgy'

import { gql } from '../../../../../../../__generated__/graphql/catalog'
import { useUserPreferenceForCategory } from '../../../../../../_hooks/useUserPreferenceForCategory'
import { useLocalization } from '../../../../../../_providers/LocalizationProvider.client'
import { useTranslation } from '../../../../../../_providers/TranslationProvider.client'
import { useUser } from '../../../../../../_providers/UserProvider.client'

import {
  type MeasureTip,
  SizeChartHowToMeasure,
  SizeChartMeasurementSummary,
  SizeChartSizeSelect,
  SizeChartUnitToggle,
} from './sizeChartComponents'
import { type ParsedSizeChart, buildSizeChartSummary, defaultRegionLabel } from './sizeChartData'
import { SizeChartSizeTable } from './SizeChartSizeTable'
import * as Styles from './SizeChartSizingSection.styles'

export type { MeasureTip }

/** One sizing grid, headed by its own subtitle, inside the shared sizing section. */
export type SizeChartSizingTable = {
  chart: ParsedSizeChart
  id: string
  subtitle: string | null | undefined
}

const sizeChartVariantGroupQuery = gql(`
  query FetchSizeChartVariantGroup($handle: String!) {
    productCatalogProduct(handle: $handle) {
      id
      variantGroup {
        id
        gender
        primaryCategory {
          id
          key
        }
      }
    }
  }
`)

export type SizeChartSizingSectionProps = {
  measureTips: readonly MeasureTip[] | null | undefined
  onToggleUnit: () => void
  productHandle: string
  showMetric: boolean
  tables: readonly SizeChartSizingTable[]
  title: string | null | undefined
}

export const SizeChartSizingSection: FC<SizeChartSizingSectionProps> = ({
  measureTips,
  onToggleUnit,
  productHandle,
  showMetric,
  tables,
  title,
}) => {
  const { sizesSection } = useTranslation()
  const strings = sizesSection.sizeChart

  const [chosenSize, setChosenSize] = useState<string | null>(null)
  const [region, setRegion] = useState<string | null>(null)
  const primaryChart = tables[0]?.chart
  const hasUnitVariants = tables.some(table => table.chart.hasUnitVariants)

  const { user } = useUser()
  const { data: variantGroupData } = useQuery(sizeChartVariantGroupQuery, {
    skip: !productHandle || !user,
    variables: { handle: productHandle },
  })
  const variantGroup = variantGroupData?.productCatalogProduct.variantGroup
  const { preferredSizeId } = useUserPreferenceForCategory({
    gender: variantGroup?.gender,
    categoryKey: variantGroup?.primaryCategory.key,
  })
  const preferredSize = preferredSizeId
    ? primaryChart?.sizes.find(
        size => size.trim().toLowerCase() === preferredSizeId.trim().toLowerCase()
      )
    : undefined
  const selectedSize = chosenSize ?? preferredSize ?? ''

  const { region: shopRegion } = useLocalization()
  const activeRegion = region ?? (primaryChart ? defaultRegionLabel(primaryChart, shopRegion) : '')
  const summaryEntries = primaryChart
    ? buildSizeChartSummary({ chart: primaryChart, selectedRegion: activeRegion, selectedSize })
    : []

  if (!primaryChart) return null

  return (
    <>
      <Styles.SectionHeader>
        <TypeStyleHeadlineExtraSmall asTag='h2'>{title}</TypeStyleHeadlineExtraSmall>
        {hasUnitVariants && (
          <SizeChartUnitToggle onToggleUnit={onToggleUnit} showMetric={showMetric} />
        )}
      </Styles.SectionHeader>

      <SizeChartSizeSelect
        onChange={setChosenSize}
        sizes={primaryChart.sizes}
        value={selectedSize}
      />

      <SizeChartMeasurementSummary entries={summaryEntries} />

      {tables.map((table, tableIndex) => (
        <Styles.SizingTable key={table.id}>
          <Styles.TableHeaderRow>
            <Styles.TableHeaderLabel>
              {table.subtitle || strings.bodyMeasurements}
            </Styles.TableHeaderLabel>
            {/* The preferences link only appears on the first row */}
            {tableIndex === 0 && (
              <Styles.SizePreferencesLink href={'/myfigs/preferences'}>
                {strings.setMySizePreferences}
              </Styles.SizePreferencesLink>
            )}
          </Styles.TableHeaderRow>

          <SizeChartSizeTable
            chart={table.chart}
            highlightedSize={selectedSize}
            onRegionChange={setRegion}
            selectedRegion={activeRegion}
          />
        </Styles.SizingTable>
      ))}

      <SizeChartHowToMeasure measureTips={measureTips} />
    </>
  )
}
